(function () {
  "use strict";

  var BOUND = "data-ecomato-cart-bound";

  function moneyFormat(cents, format, currency) {
    var amount = (Number(cents) || 0) / 100;
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, format);
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
      }).format(amount);
    } catch (err) {
      return "$" + amount.toFixed(2);
    }
  }

  function fetchCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(function (res) {
      if (!res.ok) throw new Error("cart fetch failed");
      return res.json();
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error("cart update failed");
      return res.json();
    });
  }

  function updateBadges(count) {
    document.querySelectorAll("[data-ecomato-cart-count]").forEach(function (el) {
      var n = Number(count) || 0;
      el.textContent = String(n);
      if (n > 0) {
        el.removeAttribute("hidden");
      } else {
        el.setAttribute("hidden", "");
      }
    });
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /**
   * Read a boolean-ish data attribute. Missing attribute -> `fallback`.
   * Present attribute -> `false` only when its value is the literal string
   * "false" (Liquid renders booleans as `true`/`false` strings), otherwise `true`.
   * Exported behavior is unit-testable: readBoolAttr(el, "data-x", true) with
   * no attribute returns true; with `data-x="false"` returns false.
   */
  function readBoolAttr(el, attr, fallback) {
    if (!el || !el.hasAttribute(attr)) return fallback;
    return el.getAttribute(attr) !== "false";
  }

  function CartDrawer(root) {
    this.root = root;
    this.panel = root.querySelector("[data-ecomato-cart-panel]");
    this.lines = root.querySelector("[data-ecomato-cart-lines]");
    this.empty = root.querySelector("[data-ecomato-cart-empty]");
    this.footer = root.querySelector("[data-ecomato-cart-footer]");
    this.subtotal = root.querySelector("[data-ecomato-cart-subtotal]");
    this.shipping = root.querySelector("[data-ecomato-cart-shipping]");
    this.shippingMsg = root.querySelector("[data-ecomato-cart-shipping-msg]");
    this.shippingFill = root.querySelector("[data-ecomato-cart-shipping-fill]");
    this.moneyFormat = root.getAttribute("data-money-format") || "${{amount}}";
    this.currency = root.getAttribute("data-currency") || "USD";
    this.threshold = Number(root.getAttribute("data-free-shipping-threshold") || 0);
    this.shippingTemplate =
      root.getAttribute("data-free-shipping-message") ||
      "You're {amount} away from free shipping";
    this.shippingSuccess =
      root.getAttribute("data-free-shipping-success") ||
      "You've unlocked free shipping!";
    // Header setting `open_cart_after_add` (default true). When false, adds
    // still update the badge/lines but never pop the drawer open on their own.
    this.openAfterAdd = readBoolAttr(root, "data-open-cart-after-add", true);
    this.open = false;
    this.busy = false;
    this.loading = false;
    this.lastFocus = null;
    this.bind();
  }

  /** True unless the merchant explicitly disabled auto-open via the header setting. */
  CartDrawer.prototype.shouldOpenAfterAdd = function () {
    return this.openAfterAdd !== false;
  };

  CartDrawer.prototype.bind = function () {
    var self = this;
    if (this.root.getAttribute(BOUND) === "true") return;
    this.root.setAttribute(BOUND, "true");

    document.addEventListener("click", function (event) {
      var openBtn = event.target.closest("[data-ecomato-cart-open]");
      if (openBtn) {
        event.preventDefault();
        self.show();
        return;
      }
      var closeBtn = event.target.closest("[data-ecomato-cart-close]");
      if (closeBtn && self.root.contains(closeBtn)) {
        event.preventDefault();
        self.hide();
        return;
      }
      var addBtn = event.target.closest("[data-ecomato-cart-add]");
      if (addBtn && self.root.contains(addBtn)) {
        event.preventDefault();
        var variantId = addBtn.getAttribute("data-variant-id");
        if (variantId) self.addItem(variantId, 1);
        return;
      }
      var qtyBtn = event.target.closest("[data-ecomato-cart-qty]");
      if (qtyBtn && self.root.contains(qtyBtn)) {
        event.preventDefault();
        var key = qtyBtn.getAttribute("data-key");
        var delta = Number(qtyBtn.getAttribute("data-delta") || 0);
        var current = Number(qtyBtn.getAttribute("data-qty") || 0);
        if (key) self.changeQty(key, Math.max(0, current + delta));
        return;
      }
      var removeBtn = event.target.closest("[data-ecomato-cart-remove]");
      if (removeBtn && self.root.contains(removeBtn)) {
        event.preventDefault();
        var removeKey = removeBtn.getAttribute("data-key");
        if (removeKey) self.changeQty(removeKey, 0);
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && self.open) {
        self.hide();
      }
    });

    document.addEventListener("ecomato:cart:open", function (event) {
      // Buyboxes/product forms dispatch this after a successful add. Respect
      // `shouldOpenAfterAdd()` here so `open_cart_after_add: false` stays
      // silent for automatic adds, but always honor an explicit
      // `{ detail: { force: true } }` (e.g. a merchant-triggered "view cart").
      // Manual `[data-ecomato-cart-open]` clicks are handled separately above
      // and always call show() regardless of this setting.
      var forced = Boolean(event && event.detail && event.detail.force);
      if (!forced && !self.shouldOpenAfterAdd()) return;
      self.show();
    });

    document.addEventListener("ecomato:cart:refresh", function () {
      self.refresh();
    });
  };

  CartDrawer.prototype.format = function (cents) {
    return moneyFormat(cents, this.moneyFormat, this.currency);
  };

  CartDrawer.prototype.setLoading = function (loading) {
    this.loading = Boolean(loading);
    this.root.classList.toggle("is-loading", this.loading);
    if (this.loading) {
      if (this.empty) this.empty.hidden = true;
      if (this.footer) this.footer.hidden = true;
      if (this.lines && !this.lines.querySelector("[data-ecomato-cart-skeleton]")) {
        this.lines.innerHTML =
          '<li class="ecomato-warmly-v2-cart-drawer__skeleton" data-ecomato-cart-skeleton aria-hidden="true">' +
          '<div class="ecomato-warmly-v2-cart-drawer__skeleton-line"></div>' +
          '<div class="ecomato-warmly-v2-cart-drawer__skeleton-line"></div>' +
          "</li>";
      }
    }
  };

  CartDrawer.prototype.show = function (opts) {
    var self = this;
    var options = opts || {};
    // Prevent double-open: if already open, just refresh in place instead of
    // re-triggering the open animation/focus flash.
    if (this.open) {
      if (!options.skipRefresh) this.refresh();
      return;
    }
    this.lastFocus = document.activeElement;
    this.root.hidden = false;
    this.root.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
    if (options.loading) this.setLoading(true);
    requestAnimationFrame(function () {
      self.root.classList.add("is-open");
      self.open = true;
      var close = self.root.querySelector("[data-ecomato-cart-close]");
      if (close) close.focus();
    });
    if (options.skipRefresh) return;
    this.refresh();
  };

  CartDrawer.prototype.hide = function () {
    var self = this;
    this.root.classList.remove("is-open");
    this.open = false;
    document.documentElement.style.overflow = "";
    window.setTimeout(function () {
      if (!self.open) {
        self.root.hidden = true;
        self.root.setAttribute("aria-hidden", "true");
      }
    }, 260);
    if (this.lastFocus && typeof this.lastFocus.focus === "function") {
      this.lastFocus.focus();
    }
  };

  CartDrawer.prototype.refresh = function () {
    var self = this;
    if (!this.lines || !this.lines.querySelector("[data-ecomato-cart-line], [data-ecomato-cart-skeleton]")) {
      this.setLoading(true);
    }
    return fetchCart()
      .then(function (cart) {
        self.setLoading(false);
        self.render(cart);
      })
      .catch(function () {
        self.setLoading(false);
        /* keep last known UI */
      });
  };

  CartDrawer.prototype.renderShipping = function (cart) {
    if (!this.shipping || !this.threshold) return;
    var total = Number(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price) || 0;
    var remaining = Math.max(0, this.threshold - total);
    var pct = Math.min(100, Math.round((total / this.threshold) * 100));
    if (this.shippingFill) this.shippingFill.style.width = pct + "%";
    if (remaining <= 0) {
      this.shipping.setAttribute("data-unlocked", "true");
      if (this.shippingMsg) this.shippingMsg.textContent = this.shippingSuccess;
    } else {
      this.shipping.setAttribute("data-unlocked", "false");
      if (this.shippingMsg) {
        this.shippingMsg.textContent = this.shippingTemplate.replace(
          "{amount}",
          this.format(remaining),
        );
      }
    }
  };

  CartDrawer.prototype.renderLine = function (item) {
    var img = item.image
      ? '<img src="' +
        escapeHtml(item.image) +
        '" alt="" width="72" height="72" loading="lazy">'
      : "";
    var variant =
      item.variant_title && item.variant_title !== "Default Title"
        ? '<p class="ecomato-warmly-v2-cart-drawer__line-variant">' +
          escapeHtml(item.variant_title) +
          "</p>"
        : "";
    return (
      '<li class="ecomato-warmly-v2-cart-drawer__line" data-ecomato-cart-line data-key="' +
      escapeHtml(item.key) +
      '">' +
      '<div class="ecomato-warmly-v2-cart-drawer__line-img">' +
      img +
      "</div>" +
      '<div class="ecomato-warmly-v2-cart-drawer__line-meta">' +
      '<a class="ecomato-warmly-v2-cart-drawer__line-title" href="' +
      escapeHtml(item.url || "#") +
      '">' +
      escapeHtml(item.product_title || item.title) +
      "</a>" +
      variant +
      '<p class="ecomato-warmly-v2-cart-drawer__line-price">' +
      this.format(item.final_line_price != null ? item.final_line_price : item.line_price) +
      "</p>" +
      '<div class="ecomato-warmly-v2-cart-drawer__line-actions">' +
      '<div class="ecomato-warmly-v2-cart-drawer__qty">' +
      '<button type="button" data-ecomato-cart-qty data-key="' +
      escapeHtml(item.key) +
      '" data-qty="' +
      item.quantity +
      '" data-delta="-1" aria-label="Decrease quantity">−</button>' +
      "<span>" +
      item.quantity +
      "</span>" +
      '<button type="button" data-ecomato-cart-qty data-key="' +
      escapeHtml(item.key) +
      '" data-qty="' +
      item.quantity +
      '" data-delta="1" aria-label="Increase quantity">+</button>' +
      "</div>" +
      '<button type="button" class="ecomato-warmly-v2-cart-drawer__remove" data-ecomato-cart-remove data-key="' +
      escapeHtml(item.key) +
      '">Remove</button>' +
      "</div></div></li>"
    );
  };

  CartDrawer.prototype.render = function (cart) {
    updateBadges(cart.item_count);
    this.renderShipping(cart);
    if (this.subtotal) {
      this.subtotal.textContent = this.format(
        cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price,
      );
    }
    var items = cart.items || [];
    if (!items.length) {
      if (this.empty) this.empty.hidden = false;
      if (this.footer) this.footer.hidden = true;
      if (this.lines) this.lines.innerHTML = "";
      return;
    }
    if (this.empty) this.empty.hidden = true;
    if (this.footer) this.footer.hidden = false;
    if (this.lines) {
      this.lines.innerHTML = items
        .map(function (item) {
          return this.renderLine(item);
        }, this)
        .join("");
    }
  };

  CartDrawer.prototype.changeQty = function (key, quantity) {
    var self = this;
    if (this.busy) return;
    this.busy = true;
    postJson("/cart/change.js", { id: key, quantity: quantity })
      .then(function (cart) {
        self.render(cart);
      })
      .catch(function () {
        return self.refresh();
      })
      .finally(function () {
        self.busy = false;
      });
  };

  CartDrawer.prototype.mergeAddResponse = function (payload, qty) {
    // /cart/add.js returns the line item (or {items:[...]}) — paint immediately.
    var items = [];
    if (!payload) return null;
    if (Array.isArray(payload.items)) items = payload.items;
    else if (payload.key || payload.id || payload.variant_id) items = [payload];
    if (!items.length) return null;

    var optimistic = {
      item_count: 0,
      items: [],
      items_subtotal_price: 0,
      total_price: 0,
    };
    // Keep existing lines if we already rendered a cart.
    var existing = [];
    if (this.lines) {
      this.lines.querySelectorAll("[data-ecomato-cart-line]").forEach(function () {});
    }
    // Prefer full cart from sections later; for now just show the added lines.
    items.forEach(function (item) {
      optimistic.items.push(item);
      optimistic.item_count += Number(item.quantity) || qty || 1;
      optimistic.items_subtotal_price +=
        Number(item.final_line_price != null ? item.final_line_price : item.line_price) || 0;
    });
    optimistic.total_price = optimistic.items_subtotal_price;
    return optimistic;
  };

  CartDrawer.prototype.addItem = function (variantId, quantity) {
    var self = this;
    if (this.busy) return;
    this.busy = true;
    var qty = quantity || 1;
    var autoOpen = this.shouldOpenAfterAdd();
    var wasOpen = this.open;

    // Only pre-open (optimistic skeleton) when this integration is allowed to
    // auto-open the drawer. Integrations with `open_cart_after_add: false`
    // must never flash the drawer open while the add request is in flight —
    // they still get their badge/line updates from render() below.
    if (autoOpen && !this.open) {
      var hasLines =
        this.lines && this.lines.querySelector("[data-ecomato-cart-line]");
      self.show({ loading: !hasLines, skipRefresh: true });
    }
    postJson("/cart/add.js", { id: Number(variantId), quantity: qty })
      .then(function (added) {
        var optimistic = self.mergeAddResponse(added, qty);
        if (optimistic) {
          self.setLoading(false);
          // If we only received the new line, still show it instantly, then reconcile.
          if (!self.lines || !self.lines.querySelector("[data-ecomato-cart-line]")) {
            self.render(optimistic);
          } else {
            // Append/replace by key when possible without wiping the drawer.
            optimistic.items.forEach(function (item) {
              if (!item || !item.key || !self.lines) return;
              var existing = self.lines.querySelector(
                '[data-ecomato-cart-line][data-key="' + item.key + '"]',
              );
              var html = self.renderLine(item);
              if (existing) {
                existing.outerHTML = html;
              } else {
                self.lines.insertAdjacentHTML("afterbegin", html);
              }
            });
            if (self.empty) self.empty.hidden = true;
            if (self.footer) self.footer.hidden = false;
            updateBadges(
              (Number((document.querySelector("[data-ecomato-cart-count]") || {}).textContent) || 0) +
                qty,
            );
          }
        }
        // Reconcile with authoritative cart (keys, discounts, shipping bar).
        return fetchCart().then(function (cart) {
          self.setLoading(false);
          // ALWAYS update badges/render on success, even when this add isn't
          // allowed to auto-open the drawer — mini-cart counts must stay accurate.
          self.render(cart);
          if (autoOpen && !self.open) {
            self.show({ skipRefresh: true });
          }
        });
      })
      .catch(function () {
        // On failure: never leave the drawer open from an optimistic
        // pre-open, and don't open it now either.
        if (autoOpen && !wasOpen && self.open) {
          self.hide();
        }
        return self.refresh();
      })
      .finally(function () {
        self.busy = false;
      });
  };

  function boot(scope) {
    var root = (scope || document).querySelector("[data-ecomato-cart-drawer]");
    if (!root) return;
    if (!root._ecomatoCartDrawer) {
      root._ecomatoCartDrawer = new CartDrawer(root);
    }
    fetchCart()
      .then(function (cart) {
        updateBadges(cart.item_count);
        root._ecomatoCartDrawer.renderShipping(cart);
      })
      .catch(function () {});
  }

  /** Intercept Warmly product forms so ATC opens the drawer instead of /cart. */
  function bindAjaxProductForms() {
    if (document.documentElement.dataset.ecomatoCartAjaxBound === "true") return;
    document.documentElement.dataset.ecomatoCartAjaxBound = "true";

    document.addEventListener(
      "submit",
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== "FORM") return;
        if (!form.classList.contains("quick-add-form") && !form.hasAttribute("data-ecomato-ajax-cart")) {
          return;
        }
        var drawer = document.querySelector("[data-ecomato-cart-drawer]");
        if (!drawer || !drawer._ecomatoCartDrawer) return;

        var idInput = form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) return;

        event.preventDefault();
        var qtyInput = form.querySelector('[name="quantity"]');
        var qty = qtyInput ? Number(qtyInput.value) || 1 : 1;
        drawer._ecomatoCartDrawer.addItem(idInput.value, qty);
      },
      true,
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot(document);
      bindAjaxProductForms();
    });
  } else {
    boot(document);
    bindAjaxProductForms();
  }

  document.addEventListener("shopify:section:load", function (event) {
    if (event.target) boot(event.target);
  });
})();
